<template>
  <div
    v-if="showNotificationBar"
    ref="limitingNotificationBar"
    class="limiting-notification-bar"
    :class="[
      { 'limiting-notification-bar--minimal': minimalMode },
      { 'limiting-notification-bar--shopping-open': position.list === `cl` },
    ]">
    <div class="limiting-notification-bar__content">
      <span class="limiting-notification-bar__title">
        {{ t.notificationBar.title }}
      </span>
      <template v-if="position.list !== `cl`">
        <LimitingPositionDisplay
          class="limiting-notification-bar__position"
          :loading="loading"
          order="row"
          :position="position" />
      </template>
      <template v-if="position.list === `cl`">
        <LimitingShoppingTimeDisplay />
      </template>
      <template v-if="position.list !== `cl`">
        <CustomButton
          class="limiting-notification-bar__button"
          mode="light"
          :text="t.general.moreInfo"
          @click="goToInformationPage" />
        <a
          class="limiting-notification-bar__link"
          :href="exclusiveShoppingUrl">{{ t.general.moreInfo }}</a>
      </template>
      <template v-if="position.list === `cl`">
        <span class="limiting-notification-bar__open">{{ t.general.cartUnlocked }}</span>
      </template>
    </div>
  </div>
</template>

<script>
  import config from '../../../../config';
  import CartHelper from '../../../helpers/CartHelper';
  import CookieHelper from '../../../helpers/CookieHelper';
  import FetchHelper from '../../../helpers/OldFetchHelper';
  import JWTHelper from '../../../helpers/JWTHelper';
  import CustomButton from '../../layout/buttons/CustomButton';
  import LimitingPositionDisplay from '../LimitingPositionDisplay';
  import LimitingShoppingTimeDisplay from '../LimitingShoppingTimeDisplay';
  import DomEventHelper from '../../../helpers/DomEventHelper';
  import EventListenerHelper from '../../../helpers/EventListenerHelper';

  export default {
    name: `LimitingNotificationBar`,
    components: {
      CustomButton,
      LimitingPositionDisplay,
      LimitingShoppingTimeDisplay,
    },
    data() {
      return {
        addToCartRetryPayload: null,
        body: document.querySelector(`body`),
        dispatchActivationEvent: false,
        exclusiveShoppingUrl: config.wpUrls.exclusiveShopping,
        limitingActive: false,
        loading: true,
        minimalMode: false,
        mountedDocumentEventListeners: [
          { callback: this.activateLimiting, name: `rin-limiting-active` },
          { callback: this.closeExistingShoppingConnection, name: `rin-limiting-not-active` },
          { callback: this.checkoutCompleted, name: `rin-limiting-checkout-completed` },
          { callback: this.storeAddToCartToRetry, name: `rin-limiting-add2cart-retry` },
        ],
        mountedWindowEventListeners: [
          { callback: this.fireDispatchActivationEvent, name: `DOMContentLoaded` },
          { callback: this.resizeListeners, name: `resize` },
          { callback: this.updateDisplayMode, name: `scroll` },
        ],
        position: {
          current: 0,
          list: `ll`,
          total: 0,
        },
        showNotificationBar: false,
        t: window.vueLimitingTranslations, // included in header.php
        webSocket: null,
        webSocketEvents: [
          { callback: this.handleWebSocketMessage, name: `message` },
          { callback: this.handleWebSocketOpenEvent, name: `open` },
        ],
      };
    },
    async mounted() {
      EventListenerHelper.setUpEventListeners(this.mountedDocumentEventListeners);
      const bouncerActive = await FetchHelper.get({ path: `${config.api.paths.configs.basepath}${config.api.paths.configs.paths.bouncer}` });
      if(bouncerActive.data.active) {
        if((CookieHelper.getBouncerShoppingJWT() || CookieHelper.getBouncerWebSocketConnectionJWT())) {
          if(await this.checkCookieValidity()) {
            this.dispatchActivationEvent = true;
            await this.activateLimiting();
          }
        }
      } else {
        this.cleanUpEverything();
      }
    },
    beforeDestroy() {
      this.removeEventListeners();
      this.cleanUpWebSocketConnection();
    },
    methods: {
      async activateLimiting() {
        this.limitingActive = true;
        this.showNotificationBar = true;
        await this.setUp();
      },
      async allowShopping(jwt, instantShopping = false) {
        CookieHelper.removeBouncerWebSocketConnectionJWT();
        CookieHelper.setBouncerShoppingJWT(jwt);
        if(!CookieHelper.getBouncerShoppingStartTime()) {
          CookieHelper.setBouncerShoppingStartTime(Date.now());
        }

        if(instantShopping && this.addToCartRetryPayload) {
          await CartHelper.addItemToCart(
            { amount: this.addToCartRetryPayload.amount, matchcode: this.addToCartRetryPayload.matchcode },
            this.addToCartRetryPayload.cartId,
          );
          this.addToCartRetryPayload = null;
        }
        this.createWebSocketConnection();
        this.position.list = `cl`;
        this.$nextTick(this.setLimitingBodyMarginTop);

        DomEventHelper.fireEvent({ detail: this.position, name: `rin-limiting-position-update` });
        DomEventHelper.fireEvent({ name: `rin-limiting-shopping-allowed` });
      },
      checkCookieValidity() {
        const bouncerShoppingJWT = CookieHelper.getBouncerShoppingJWT();
        if(bouncerShoppingJWT) {
          const decodedBouncerShoppingJWT = JWTHelper.parseJwt(bouncerShoppingJWT);
          if(decodedBouncerShoppingJWT.exp * 1000 > Date.now()) {
            return true;
          }
        }
        const bouncerWebSocketJWT = CookieHelper.getBouncerWebSocketConnectionJWT();
        if(bouncerWebSocketJWT) {
          const decodedBouncerWebSocketJWT = JWTHelper.parseJwt(bouncerWebSocketJWT);
          if(decodedBouncerWebSocketJWT.exp * 1000 > Date.now()) {
            return true;
          }
        }
        this.cleanUpEverything();
        return false;
      },
      async checkoutCompleted() {
        const fetchConfig = {
          baseURL: config.bouncer.api.baseurl,
          body: { id: CookieHelper.getBouncerShoppingJWT() },
          path: config.bouncer.api.paths.finish,
        };
        await FetchHelper.post(fetchConfig);
        this.cleanUpEverything(true);
      },
      cleanUpEverything(checkoutComplete = false) {
        CookieHelper.removeBouncerShoppingJWT();
        CookieHelper.removeBouncerShoppingStartTime();
        CookieHelper.removeBouncerWebSocketConnectionJWT();
        this.removeLimitingBodyClass();
        this.removeLimitingBodyMarginTop();
        if(this.webSocket) {
          this.webSocket.close();
          EventListenerHelper.teardownEventListeners(this.webSocketEvents, this.webSocket);
          this.webSocket = null;
        }
        this.position = {
          current: -1,
          list: `ll`,
          total: 0,
        };
        DomEventHelper.fireEvent({ detail: this.position, name: `rin-limiting-position-update` });
        if(checkoutComplete) {
          DomEventHelper.fireEvent({ name: `rin-limiting-finish-order` });
        }
      },
      cleanUpWebSocketConnection() {
        if(this.webSocket) {
          EventListenerHelper.teardownEventListeners(this.webSocketEvents, this.webSocket);
          this.webSocket.close();
          this.webSocket = null;
        }
      },
      closeExistingShoppingConnection() {
        this.cleanUpEverything();
        this.$swal(``, this.t.general.timeIsUp, `info`);
        this.showNotificationBar = false;
      },
      createWebSocketConnection() {
        if(this.webSocket) {
          return;
        }

        const webSocketJWT = CookieHelper.getBouncerWebSocketConnectionJWT() || CookieHelper.getBouncerShoppingJWT();
        this.webSocket = new WebSocket(`${config.bouncer.ws.baseurl}?id=${webSocketJWT}`);
        EventListenerHelper.setUpEventListeners(this.webSocketEvents, this.webSocket);
      },
      fireDispatchActivationEvent() {
        if(this.dispatchActivationEvent) {
          DomEventHelper.fireEvent({ name: `rin-limiting-still-active` });
          this.dispatchActivationEvent = false;
        }
      },
      async getInformationFromBouncer() {
        await this.requestAccessFromBouncer();
        const shoppingToken = CookieHelper.getBouncerShoppingJWT();
        if(shoppingToken) {
          this.createWebSocketConnection();
          await this.allowShopping(shoppingToken);
        } else {
          await this.getListStatus();
        }
      },
      async getListStatus() {
        const userToken = JWTHelper.parseJwt(CookieHelper.getBouncerWebSocketConnectionJWT());
        if(!userToken) {
          this.getInformationFromBouncer();
          return;
        }
        // token expired
        if(userToken.exp < Date.now() / 1000) {
          CookieHelper.removeBouncerWebSocketConnectionJWT();
          await this.requestAccessFromBouncer();
          return;
        }
        const userId = userToken.id;
        const fetchConfig = {
          baseURL: config.bouncer.api.baseurl,
          ignoreCredentials: true,
          path: config.bouncer.api.paths.status,
        };
        const fetchResult = await FetchHelper.get(fetchConfig);

        let firstQueuePosition = fetchResult.firstShortlistId;
        if(firstQueuePosition === 0) {
          firstQueuePosition = fetchResult.longlistCursorStartPosition;
        }
        if(fetchResult.success) {
          this.position.current = userId - firstQueuePosition + 1;
          this.position.list = userId <= fetchResult.lastShortlistId ? `sl` : `ll`;
          DomEventHelper.fireEvent({ detail: this.position, name: `rin-limiting-position-update` });

          if(userId < fetchResult.longlistCursorStartPosition) {
            this.createWebSocketConnection();
          } else {
            window.setTimeout(async () => {
              await this.getListStatus();
            }, 15000);
          }
        }
      },
      goToInformationPage() {
        window.location.href = config.wpUrls.exclusiveShopping;
      },
      async handleWebSocketMessage(event) {
        const data = JSON.parse(event.data);
        if(!data.success) {
          this.webSocket.close();
          CookieHelper.removeBouncerWebSocketConnectionJWT();
          await this.requestAccessFromBouncer();
        }
        if(data.position) {
          this.position.current = data.position;
        }
        if(data.jwt) {
          await this.allowShopping(data.jwt);
        }
      },
      handleWebSocketOpenEvent() {
        const waitingToken = CookieHelper.getBouncerWebSocketConnectionJWT();
        if(waitingToken) {
          this.webSocket.send(JSON.stringify({ id: waitingToken }));
        }
      },
      removeEventListeners() {
        EventListenerHelper.teardownEventListeners(this.mountedDocumentEventListeners);
        EventListenerHelper.teardownEventListeners(this.mountedWindowEventListeners, window);
      },
      removeLimitingBodyClass() {
        this.body.classList.remove(`rin-limiting-active`);
      },
      removeLimitingBodyMarginTop() {
        this.body.style.removeProperty(`margin-top`);
      },
      async requestAccessFromBouncer() {
        if(CookieHelper.getBouncerWebSocketConnectionJWT() || CookieHelper.getBouncerShoppingJWT()) {
          return;
        }

        const fetchConfig = {
          baseURL: config.bouncer.api.baseurl,
          ignoreCredentials: true,
          path: config.bouncer.api.paths.newUser,
        };

        const fetchResult = await FetchHelper.post(fetchConfig);
        if(fetchResult.success) {
          if(fetchResult.type === `jwt`) {
            await this.allowShopping(fetchResult.jwt, true);
          }
          if(fetchResult.type === `queue`) {
            this.addToCartRetryPayload = null;
            const jwt = fetchResult.id;
            CookieHelper.setBouncerWebSocketConnectionJWT(jwt);
          }
        }
      },
      resizeListeners() {
        _.debounce(() => {
          this.setLimitingBodyMarginTop();
          this.updateDisplayMode();
        }, 250);
      },
      setEventListeners() {
        EventListenerHelper.setUpEventListeners(this.mountedWindowEventListeners, window);
        EventListenerHelper.setUpEventListeners([{ callback: this.closeExistingShoppingConnection, name: `rin-limiting-time-is-up` }]);
      },
      setLimitingBodyClass() {
        this.body.classList.add(`rin-limiting-active`);
      },
      setLimitingBodyMarginTop() {
        if(!this.$refs.limitingNotificationBar) {
          return;
        }
        const height = this.$refs.limitingNotificationBar.clientHeight;
        this.body.style.marginTop = `${height}px`;
      },
      async setUp() {
        if(this.limitingActive) {
          this.setLimitingBodyClass();
          this.setEventListeners();
          await this.getInformationFromBouncer();
          this.loading = false;
          this.$nextTick(this.setLimitingBodyMarginTop);
        }
      },
      storeAddToCartToRetry(event) {
        this.addToCartRetryPayload = event.detail;
      },
      updateDisplayMode() {
        if(!this.$refs.limitingNotificationBar) {
          return;
        }
        // 720px is the breakpoint where the notification bar switches to desktop mode
        if(window.innerWidth <= 720) {
          const scrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
          const headerHeight = document.querySelector(`header.rin-header.rin-header--mobile`).clientHeight;
          if(scrollPosition >= headerHeight && !this.$refs.limitingNotificationBar.classList.contains(`limiting-notification-bar--minimal`)) {
            this.minimalMode = true;
          } else if(scrollPosition < headerHeight && this.$refs.limitingNotificationBar.classList.contains(`limiting-notification-bar--minimal`)) {
            this.minimalMode = false;
          }
        } else if(this.minimalMode) {
          this.minimalMode = false;
        }
      },
    },
  };
</script>

<style lang="less" scoped>
  @import '../../../assets/styles/rin-variables';

  .limiting-notification-bar {
    align-items: center;
    background-color: @colorBlack;
    color: @colorWhite;
    display: flex;
    flex-direction: row;
    justify-content: center;
    left: 0;
    min-height: @limitingNotificationContainerMinHeight;
    position: fixed;
    right: 0;
    top: 0;
    transition: min-height .125s, background-color .25s, color .25s;
    z-index: 99999;

    @media @fromTablet {
      height: @limitingNotificationContainerMinHeight; // for IE, since no mobile IE only set the height for tablet and above
    }

    &__button {
      font-size: 12px;
      min-width: auto;
      padding: 15px 30px;

      // instead of visible-desktop class because the css for it is parsed later
      // causing issues with the height calculation
      @media (max-width: @mobile-threshold-max) {
        display: none !important;
      }
    }

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      font-family: @fontRegular;
      font-size: @fontSizeM-L;
      max-width: @limitingNotificationContainerMaxWidth;
      padding: 10px 20px 20px 20px;
      position: relative;
      text-align: center;
      width: 100%;

      @media @fromTablet {
        flex-direction: row;
        justify-content: space-between;
        padding: 0 20px;
      }
    }

    &__link {
      border-bottom: 1px solid @colorWhite;
      color: @colorWhite;
      display: inline-block;
      font-size: 11px;
      height: 17px;
      letter-spacing: 2.75px;
      line-height: 1;
      padding-bottom: 5px;
      text-transform: uppercase;
      transition: height .125s;

      // instead of visible-mobile class because the css for it is parsed later
      // causing issues with the height calculation
      @media (min-width: @tablet-threshold-min) {
        display: none !important;
      }
    }

    &__open {
      font-size: 12px;
      letter-spacing: 1px;
      line-height: 1.667;
      margin-top: 5px;
      max-width: 200px;

      @media @fromTablet {
        margin-top: 0;
        text-align: right;
      }
    }

    &__position {
      padding: 0;
      transition: padding .125s;

      @media @fromTablet {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }

    &__title {
      font-family: @fontSemiBold;
      font-size: 14px;
      height: 20px;
      line-height: 1.429;
      margin-bottom: 5px;
      opacity: .75;
      transition: height .125s;

      @media @fromTablet {
        margin-bottom: 0;
      }

      @media @desktop {
        font-size: 17px;
        line-height: 1.176;
      }
    }
  }

  .limiting-notification-bar--minimal {
    min-height: 72px;

    .limiting-notification-bar__link {
      border: 0;
      height: 0;
      overflow: hidden;
      padding: 0;
    }

    .limiting-notification-bar__open,
    .limiting-notification-bar__title {
      height: 0;
      margin: 0;
      overflow: hidden;
    }
  }

  .limiting-notification-bar--shopping-open {
    background-color: @colorLimitingNotificationShoppingOpen;
    color: @colorBlack;

    .limiting-notification-bar__title {
      opacity: 1;
    }
  }
</style>
